"use client";
import type { RawProductAttribute } from "@graphql/generated/types";
import { useIsHydrated } from "@hooks/useIsHydrated";
import type { SizeLocales } from "@lib/constants/filtersConstants";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { hasNotifySizeAvailability } from "@lib/utils/notificationFeature";
import type { NotShoeSizeSelectable, ShoeSizeSelectable } from "@lib/utils/pdp/pdpShoeSizes";
import { getVariantSize } from "@lib/utils/pdp/pdpShoeSizes";
import { useStore } from "@store/index";
import { Text } from "@ecco/ui/text";
import { Skeleton } from "@ecco/ui/skeleton";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { isNumber, isString } from "lodash-es";
import dynamic from "next/dynamic";
import { useSearchParams, useParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useProductSizeInfo } from "./hooks/useProductSizeInfo";
import { useProductSizeHandlers } from "./hooks/useProductSizeHandlers";
import type { Product, ProductVariant } from "@lib/product/types";
import type { RecommendationResult } from "@lib/algolia/types";
import { Menu, MenuButton, MenuList, MenuItem } from "@ecco/ui/menu";
import { useLocale } from "use-intl";
import SizeWizardVB from "@components/size-wizard/SizeWizardVB";
import { Grid } from "@ecco/ui/grid";
import { RUM_ACTION_NAME } from "@lib/monitoring/rumActionNames";
import "./ProductBuyCardSizesVB.css";

const SizeGuide = dynamic(
    () => import("@components/ui/size-guide-modal/SizeGuidePdp").then((mod) => mod.SizeGuide),
    {
        ssr: false,
    }
);

const NotifySizeAvailability = dynamic(
    () => import("@components/commerce/product/notify-size-availability"),
    {
        ssr: false,
    }
);

interface ProductBuyCardSizesVBProps {
    product: Product;
    variants: ProductVariant[];
    attributes: RawProductAttribute[];
    productName: string;
    isStickyModal?: boolean;
    /** When set, overrides the default bottom margin on the root wrapper (e.g. PDP buy card). */
    marginBottom?: number;
    isHighlighted?: boolean;
    recommendations?: RecommendationResult[];
}

export const productBuyCardSizesWrapper = "product-buy-card-sizes-vb-wrapper";
export const TEST_ID_SHOE_SIZE_BUTTON = "shoe-size-button";

const ProductBuyCardSizesVB = ({
    product,
    variants,
    attributes,
    isStickyModal,
    productName,
    marginBottom,
    isHighlighted,
    recommendations = [],
}: ProductBuyCardSizesVBProps) => {
    const locale = useLocale();

    const sizeSuggestion = product.productAttributes.find(
        (attr) => attr.name === "SizeSuggestionProduct"
    )?.value?.[locale];

    const activeProduct = useStore((state) => state.data.pdpState.activeProduct);

    const selectedVariant = activeProduct?.variant;
    const searchParams = useSearchParams();
    const params = useParams();
    const size = searchParams.get("size");
    const system = searchParams.get("system");
    const globalShoeSizeConvention = useStore((state) => state.data.me?.globalShoeSizeConvention);
    const setGlobalShoeSizeConvention = useStore((state) => state.setGlobalShoeSizeConvention);
    const isHydrated = useIsHydrated();
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const sizeRef = useRef<HTMLDivElement>(null);

    const {
        microcopies,
        gender,
        targetGroup,
        materialGroup,
        isProbablyOneSize,
        hasAlternativeSize,
        isFootwearProduct,
        sizeLabels,
        availableConventions,
        shoeSizeSystems,
        currentlyUsedUnit,
        selectedShoeSizeSystem,
    } = useProductSizeInfo({
        variants,
        attributes,
        globalShoeSizeConvention,
    });

    const { handleSelectVariant, handleSizeSystemClick, onSelectedVariantClick } =
        useProductSizeHandlers({
            variants,
            currentlyUsedUnit,
            size,
            colorCode: activeProduct?.colorCode ?? (params.colorCode as string),
            setShowNotifyModal,
        });

    useEffect(() => {
        if (isHighlighted) {
            sizeRef?.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [isHighlighted]);

    const getLabel = useCallback(() => {
        return sizeLabels.find((label) => label.sizeData.toString() === size);
    }, [size, sizeLabels]);

    const resolveProductLabel = useCallback(
        (label: ShoeSizeSelectable | NotShoeSizeSelectable) => {
            const sizeEU = "sizeEU" in label ? label.sizeEU : "";
            const fallback = hasAlternativeSize ? sizeEU : `${sizeEU} EU`;

            const selectedConventionLabel =
                label[`size${selectedShoeSizeSystem.key}`] ||
                label[`size${selectedShoeSizeSystem.label}`];

            return isString(selectedConventionLabel) || isNumber(selectedConventionLabel)
                ? selectedConventionLabel
                : fallback;
        },
        [hasAlternativeSize, selectedShoeSizeSystem]
    );

    const getLabelText = useCallback(
        (
            label: ShoeSizeSelectable | NotShoeSizeSelectable,
            showNotify = false,
            isSelected = false
        ): JSX.Element => {
            const productLabel = resolveProductLabel(label);

            const isOutOfStock = !label.available;
            return (
                <>
                    <Flex>
                        <Text
                            p={4}
                            w="150px"
                            textDecoration={isOutOfStock ? "line-through" : "none"}
                            color={isOutOfStock ? "gray.300" : "black"}
                            fontWeight={isOutOfStock ? "normal" : "bold"}
                            textAlign="left"
                        >
                            {productLabel}
                        </Text>
                        <Text
                            p={4}
                            textDecoration={isOutOfStock ? "line-through" : "none"}
                            color={isOutOfStock ? "gray.300" : "black"}
                            fontWeight={isSelected && !isOutOfStock ? "bold" : "normal"}
                            textAlign="left"
                        >
                            {isOutOfStock
                                ? microcopies.soldOut
                                : label.stock === 1
                                  ? microcopies.OnePairLeft
                                  : microcopies.inStock}
                        </Text>
                    </Flex>
                    {showNotify && isOutOfStock && hasNotifySizeAvailability && (
                        <Flex>
                            <Text
                                as="a"
                                href="#"
                                fontSize="mobileBodyTextSmall"
                                textDecoration="underline"
                                cursor="pointer"
                                onClick={() => {
                                    setShowNotifyModal(true);
                                }}
                            >
                                {microcopies.GetNotified}
                            </Text>
                        </Flex>
                    )}
                </>
            );
        },
        [resolveProductLabel, hasNotifySizeAvailability]
    );

    useEffect(() => {
        if (selectedVariant) return;
        if (size) {
            const isExistingSize = sizeLabels.find((it) => it.sizeData.toString() === size);

            if (isExistingSize) {
                handleSelectVariant({ isAvailable: isExistingSize.available, size });
            }
        }
        if (system && availableConventions.some((it) => it === system)) {
            setGlobalShoeSizeConvention(system as SizeLocales);
        }
    }, [
        size,
        system,
        sizeLabels,
        handleSelectVariant,
        setGlobalShoeSizeConvention,
        availableConventions,
    ]);

    useEffect(() => {
        if (!isProbablyOneSize) {
            return;
        }
        const singleSize = getVariantSize(variants);
        if (!singleSize) {
            return;
        }
        void handleSelectVariant({ size: singleSize, isAvailable: true });
    }, [isProbablyOneSize, variants, handleSelectVariant]);

    if (isProbablyOneSize) {
        return (
            <Box
                mb={marginBottom !== undefined ? marginBottom : 4}
                data-testid={productBuyCardSizesWrapper}
                display="none"
            />
        );
    }

    const rootMb = marginBottom !== undefined ? marginBottom : isStickyModal ? 0 : 8;
    const selectedSizeLabel = size ? getLabel() : undefined;

    return (
        <Box
            mb={rootMb}
            ref={sizeRef}
            data-testid={productBuyCardSizesWrapper}
            data-vwo-element="product-buy-card-sizes-vb"
            display="none"
        >
            <Flex
                gap={1}
                mb={2}
                flexWrap="wrap"
                align={isStickyModal ? "flex-start" : "center"}
                justifyContent={"space-between"}
            >
                <Text fontWeight={"bold"} size={"medium"}>
                    {microcopies.sizeTitle}
                </Text>
                <Flex display={{ base: "none", md: "flex" }}>
                    <SizeGuide
                        gender={gender}
                        targetGroup={targetGroup}
                        materialGroup={materialGroup}
                        isFootwear={isFootwearProduct}
                        type={ModalTypes.sizeGuide}
                    />
                </Flex>
            </Flex>
            {!isHydrated && (
                <Box w="full" minW={0}>
                    <Skeleton w="full" h={10} />
                </Box>
            )}

            {isHydrated && (
                <Box
                    w="full"
                    minW={0}
                    borderColor={isHighlighted ? "red.500" : "none"}
                    borderStyle={isHighlighted ? "solid" : "none"}
                    borderWidth={isHighlighted ? "1px" : "0"}
                >
                    <Menu
                        matchWidth
                        placement="bottom-start"
                        modifiers={[{ name: "flip", enabled: false }]}
                    >
                        {({ onClose }) => (
                            <>
                                <MenuButton
                                    w="full"
                                    minW={0}
                                    h={10}
                                    px={4}
                                    py={2}
                                    borderRadius="md"
                                    borderWidth="1px"
                                >
                                    <Flex justifyContent="space-between" alignItems="center">
                                        {selectedSizeLabel ? (
                                            getLabelText(selectedSizeLabel, false, true)
                                        ) : (
                                            <Text fontSize="mobileBodyTextSmall">
                                                {microcopies.SelectSize}
                                            </Text>
                                        )}
                                        <ECCOIcon name="chevron" size="sm" />
                                    </Flex>
                                </MenuButton>
                                <MenuList
                                    zIndex={999}
                                    boxShadow="lg"
                                    data-vwo-element="product-buy-card-sizes-vb-menu-list"
                                >
                                    <Flex
                                        justifyContent="space-between"
                                        alignItems="center"
                                        display={{ base: "flex", md: "none" }}
                                    >
                                        <Text
                                            px={4}
                                            py={2}
                                            fontWeight="bold"
                                            fontSize="mobileBodyTextLarge"
                                            textTransform="uppercase"
                                        >
                                            {microcopies.SelectSize}
                                        </Text>
                                        <Button onClick={onClose} aria-label="Close size menu">
                                            <ECCOIcon name="close" size="sm" />
                                        </Button>
                                    </Flex>
                                    <Flex display={{ base: "flex", md: "none" }} px={4} py={2}>
                                        <SizeGuide
                                            gender={gender}
                                            targetGroup={targetGroup}
                                            materialGroup={materialGroup}
                                            isFootwear={isFootwearProduct}
                                            type={ModalTypes.sizeGuide}
                                        />
                                    </Flex>

                                    <Flex
                                        justifyContent="space-between"
                                        alignItems="center"
                                        px={4}
                                        py={2}
                                    >
                                        <SizeWizardVB
                                            selectedSize={sizeSuggestion}
                                            sizeGuideOpenProps={{
                                                gender,
                                                targetGroup,
                                                materialGroup,
                                                isFootwear: isFootwearProduct,
                                                isSocks: materialGroup?.toLowerCase() === "socks",
                                                forKids: targetGroup?.toLowerCase() === "kids",
                                            }}
                                        />
                                    </Flex>

                                    {!hasAlternativeSize && (
                                        <Grid templateColumns="repeat(2, 1fr)">
                                            {shoeSizeSystems.map((item) => {
                                                const { key, label, aria } = item;
                                                if (!key) {
                                                    return null;
                                                }
                                                const isActive = key === selectedShoeSizeSystem.key;
                                                return isHydrated ? (
                                                    <Button
                                                        key={key}
                                                        onClick={() => handleSizeSystemClick(key)}
                                                        aria-label={aria}
                                                        variant="unstyled"
                                                        borderBottomWidth={2}
                                                        borderBottomColor={
                                                            isActive ? "black" : "gray.300"
                                                        }
                                                        color={isActive ? "black" : "gray.300"}
                                                    >
                                                        {label || key}
                                                    </Button>
                                                ) : (
                                                    <Skeleton key={key} px={4} w={12} h={6} />
                                                );
                                            })}
                                        </Grid>
                                    )}
                                    <Flex maxH={300} overflowY="auto" flexDirection="column">
                                        {sizeLabels.map(
                                            (label: ShoeSizeSelectable | NotShoeSizeSelectable) => {
                                                const isSelected =
                                                    label.sizeData.toString() === size;
                                                const isOutOfStock = !label.available;
                                                return (
                                                    <MenuItem
                                                        key={label.sizeData.toString()}
                                                        as={isOutOfStock ? "div" : "button"}
                                                        borderWidth={isSelected ? 1 : 0}
                                                        borderColor={
                                                            isSelected ? "black" : "gray.300"
                                                        }
                                                        onClick={() =>
                                                            onSelectedVariantClick(
                                                                label.available,
                                                                label.sizeData.toString()
                                                            )
                                                        }
                                                        data-dd-action-name={
                                                            RUM_ACTION_NAME.SELECT_SIZE
                                                        }
                                                    >
                                                        <Flex
                                                            flex={1}
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            {getLabelText(label, true, isSelected)}
                                                        </Flex>
                                                    </MenuItem>
                                                );
                                            }
                                        )}
                                    </Flex>
                                </MenuList>
                            </>
                        )}
                    </Menu>
                </Box>
            )}
            {hasNotifySizeAvailability && showNotifyModal && selectedVariant && (
                <NotifySizeAvailability
                    isOpen={showNotifyModal}
                    onClose={() => setShowNotifyModal(false)}
                    selectedSize={
                        getProductAttributeValue(
                            selectedVariant.attributesRaw,
                            Attributes.size
                        )?.toString() ?? ""
                    }
                    selectedProductSku={selectedVariant.sku}
                    productName={productName || ""}
                    recommendations={recommendations}
                />
            )}
        </Box>
    );
};

export default ProductBuyCardSizesVB;
